<template>
  <div class="user-picker flex column gap10">
    <div class="selected-users-list" v-if="selectedUsers.length">
      <div class="selected-users-header">
        <span class="selected-count">{{$t('releaseLocales.selectedUsers')}}: {{selectedUsers.length}}</span>
      </div>
      <div class="selected-users-items flex column gap8">
        <div 
          v-for="user in selectedUsers" 
          :key="user._id" 
          class="selected-user-item flex align-center space-between"
        >
          <div class="user-info flex align-center gap12">
            <div class="user-avatar">{{getUserInitials(user)}}</div>
            <div class="user-details flex column gap2">
              <span class="user-name">{{getUserDisplayName(user)}}</span>
              <span class="user-email">{{user.email}}</span>
            </div>
          </div>
          <button class="btn-remove" @click="removeUser(user)" :title="$t('remove')">
            <span>×</span>
          </button>
        </div>
      </div>
    </div>
    
    <button class="btn big" @click="openModal">
      {{$t('add')}} {{$t('releaseLocales.users')}}
    </button>
    
    <Modal v-if="showModal" @close="showModal = false" :fullScreen="true">
      <div class="user-picker-modal flex column">
        <header class="modal-header flex align-center space-between">
          <h3>{{$t('releaseLocales.selectUsers')}}</h3>
          <button class="btn-icon" @click="showModal = false" aria-label="Close">
            <span>×</span>
          </button>
        </header>
        
        <div class="modal-content">
          <div class="search-container" v-if="!isLoading">
            <FormInput
              type="text"
              :placeholder="$t('releaseLocales.searchUsers') || 'Search users...'"
              :value="searchQuery"
              @input="val => searchQuery = val"
              class="search-input"
            />
          </div>
          
          <div v-if="isLoading" class="loading-state flex align-center justify-center">
            <p>{{$t('loading')}}</p>
          </div>
          
          <div v-else-if="filteredUsers.length" class="users-table-container">
            <table class="users-table">
              <thead>
                <tr>
                  <th class="col-checkbox">
                    <input 
                      type="checkbox" 
                      :checked="allSelected" 
                      :indeterminate="someSelected"
                      @change="toggleAllUsers"
                      class="checkbox-input"
                    />
                  </th>
                  <th class="col-name">{{$t('releaseLocales.name')}}</th>
                  <th class="col-email">{{$t('releaseLocales.email')}}</th>
                  <th class="col-mobile">{{$t('releaseLocales.mobile')}}</th>
                  <th class="col-roles">{{$t('releaseLocales.roles')}}</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="user in filteredUsers" 
                  :key="user._id"
                  :class="{selected: isUserSelected(user)}"
                  @click="toggleUser(user)"
                  class="user-row"
                >
                  <td class="col-checkbox">
                    <input 
                      type="checkbox" 
                      :checked="isUserSelected(user)"
                      @change.stop="toggleUser(user)"
                      @click.stop
                      class="checkbox-input"
                    />
                  </td>
                  <td class="col-name">
                    <div class="user-name">
                      {{getUserDisplayName(user)}}
                    </div>
                  </td>
                  <td class="col-email">
                    <div class="user-email">{{user.email || '-'}}</div>
                  </td>
                  <td class="col-mobile">
                    <div class="user-mobile">{{formatMobile(user)}}</div>
                  </td>
                  <td class="col-roles">
                    <div class="user-roles">
                      <span 
                        v-for="role in getUserOrgRoles(user)" 
                        :key="role"
                        class="role-badge"
                      >
                        {{getRoleTranslation(role)}}
                      </span>
                      <span v-if="!getUserOrgRoles(user).length" class="no-roles">-</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div v-else-if="availableUsers.length === 0" class="empty-state flex align-center justify-center">
            <p>{{$t('noItemsFound')}}</p>
          </div>
          
          <div v-else class="empty-state flex align-center justify-center">
            <p>{{$t('releaseLocales.noUsersMatchSearch') || 'No users match your search'}}</p>
          </div>
        </div>
        
        <footer class="modal-footer flex align-center justify-end gap20">
          <button class="btn big" @click="showModal = false">{{$t('cancel')}}</button>
          <button class="btn big primary" @click="confirmSelection">
            {{$t('confirm')}} ({{selectedUsers.length}})
          </button>
        </footer>
      </div>
    </Modal>
  </div>
</template>

<script>
import Modal from '@/apps/common/modules/common/cmps/Modal.vue'
import FormInput from '@/apps/common/modules/common/cmps/FormInput.vue'
import { httpService } from '@/apps/common/modules/common/services/http.service'

export default {
  name: 'UserPicker',
  components: {
    Modal,
    FormInput
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    organizationId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showModal: false,
      availableUsers: [],
      selectedUsers: [],
      isLoading: false,
      searchQuery: ''
    }
  },
  computed: {
    filteredUsers() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.availableUsers
      }
      
      const query = this.searchQuery.toLowerCase().trim()
      
      return this.availableUsers.filter(user => {
        // Search in name
        const firstName = (user.firstName || user.firstname || '').toLowerCase()
        const lastName = (user.lastName || user.lastname || '').toLowerCase()
        const fullName = `${firstName} ${lastName}`.trim()
        
        // Search in email
        const email = (user.email || '').toLowerCase()
        
        // Search in mobile
        const mobile = this.formatMobile(user).toLowerCase()
        
        // Search in roles
        const roles = this.getUserOrgRoles(user)
        const roleNames = roles.map(role => this.getRoleTranslation(role).toLowerCase()).join(' ')
        
        return fullName.includes(query) ||
               email.includes(query) ||
               mobile.includes(query) ||
               roleNames.includes(query)
      })
    },
    allSelected() {
      return this.filteredUsers.length > 0 && 
             this.filteredUsers.every(user => this.isUserSelected(user))
    },
    someSelected() {
      return this.filteredUsers.some(user => this.isUserSelected(user)) && !this.allSelected
    }
  },
  watch: {
    value: {
      immediate: true,
      async handler(newVal) {
        // Initialize selected users from prop
        if (newVal && newVal.length) {
          // Check if value is array of user objects or IDs
          if (Array.isArray(newVal) && newVal[0]?.email) {
            // Already user objects
            this.selectedUsers = [...newVal]
          } else if (Array.isArray(newVal) && typeof newVal[0] === 'string') {
            // Array of user IDs - fetch user details
            await this.fetchUsersByIds(newVal)
          } else {
            this.selectedUsers = []
          }
        } else {
          this.selectedUsers = []
        }
      }
    }
  },
  methods: {
    getUserDisplayName(user) {
      const firstName = user.firstName || user.firstname || ''
      const lastName = user.lastName || user.lastname || ''
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || user.email || user._id
    },
    
    getUserInitials(user) {
      const firstName = user.firstName || user.firstname || ''
      const lastName = user.lastName || user.lastname || ''
      const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : ''
      const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : ''
      if (firstInitial || lastInitial) {
        return `${firstInitial}${lastInitial}`
      }
      // Fallback to email initial
      return user.email ? user.email.charAt(0).toUpperCase() : '?'
    },
    
    getUserOrgRoles(user) {
      if (!user.organizations || !Array.isArray(user.organizations)) return []
      const org = user.organizations.find(org => 
        org._id?.toString() === this.organizationId.toString()
      )
      return org?.roles || []
    },
    
    getRoleTranslation(role) {
      // Try to translate the role, fallback to role name if translation not found
      const translation = this.$t(`organizationLocales.orgRoles.${role}`)
      // If translation returns the key itself, it means translation wasn't found
      if (translation === `organizationLocales.orgRoles.${role}`) {
        return role // Fallback to English role name
      }
      return translation
    },
    
    formatMobile(user) {
      if (!user) return '-'
      // Prefer formatted mobile data
      if (user.mobileData?.formatted) {
        return user.mobileData.formatted
      }
      // Clean up mobile number - remove "client" prefix if present
      let mobile = user.mobile || ''
      if (mobile.startsWith('client')) {
        mobile = mobile.replace(/^client/, '')
      }
      // Remove trailing + if present
      mobile = mobile.replace(/\+$/, '')
      return mobile || '-'
    },
    
    async fetchUsersByIds(userIds) {
      if (!userIds || !userIds.length) {
        this.selectedUsers = []
        return
      }
      
      try {
        // Fetch user details by IDs - fetch all org users and filter by IDs
        const response = await httpService.get('account', {
          organizationId: this.organizationId
        })
        // Filter to only include users whose IDs are in the userIds array
        this.selectedUsers = (response.items || []).filter(user => 
          userIds.some(id => id.toString() === user._id.toString())
        )
      } catch (err) {
        console.error('Error fetching users by IDs:', err)
        this.selectedUsers = []
      }
    },
    
    async loadAllUsers() {
      this.isLoading = true
      try {
        // Load all users in the organization
        const response = await httpService.get('account', {
          organizationId: this.organizationId
        })
        this.availableUsers = response.items || []
      } catch (err) {
        console.error('Error loading users:', err)
        this.availableUsers = []
      } finally {
        this.isLoading = false
      }
    },
    
    toggleUser(user) {
      if (this.isUserSelected(user)) {
        this.selectedUsers = this.selectedUsers.filter(u => u._id !== user._id)
      } else {
        this.selectedUsers.push(user)
      }
    },
    
    toggleAllUsers() {
      if (this.allSelected) {
        // Deselect all filtered users
        const filteredUserIds = this.filteredUsers.map(u => u._id)
        this.selectedUsers = this.selectedUsers.filter(u => !filteredUserIds.includes(u._id))
      } else {
        // Select all filtered users (merge with existing selections)
        const existingIds = this.selectedUsers.map(u => u._id)
        const newSelections = this.filteredUsers.filter(u => !existingIds.includes(u._id))
        this.selectedUsers = [...this.selectedUsers, ...newSelections]
      }
    },
    
    removeUser(user) {
      this.selectedUsers = this.selectedUsers.filter(u => u._id !== user._id)
      this.emitUpdate()
    },
    
    isUserSelected(user) {
      return this.selectedUsers.some(u => u._id === user._id)
    },
    
    confirmSelection() {
      this.emitUpdate()
      this.showModal = false
    },
    
    async openModal() {
      this.showModal = true
      this.searchQuery = '' // Reset search when opening modal
      // Load all users when modal opens
      await this.loadAllUsers()
    },
    
    emitUpdate() {
      // Emit array of user IDs
      const userIds = this.selectedUsers.map(u => u._id)
      this.$emit('input', userIds)
    }
  }
}
</script>

<style lang="scss" scoped>
.user-picker {
  .selected-users-list {
    background: var(--clr-1, #ffffff);
    border: 1px solid var(--clr-0-l-90, #e0e0e0);
    border-radius: 8px;
    overflow: hidden;
    
    .selected-users-header {
      padding: 0.75em 1em;
      background: var(--clr-0-l-98, #f8f9fa);
      border-bottom: 1px solid var(--clr-0-l-90, #e0e0e0);
      
      .selected-count {
        font-weight: 600;
        font-size: 0.9em;
        color: var(--clr-0-l-30, #495057);
      }
    }
    
    .selected-users-items {
      padding: 0.75em;
      max-height: 300px;
      overflow-y: auto;
    }
    
    .selected-user-item {
      padding: 0.75em;
      background: var(--clr-1, #ffffff);
      border: 1px solid var(--clr-0-l-95, #e9ecef);
      border-radius: 6px;
      transition: all 0.2s;
      
      &:hover {
        background: var(--clr-0-l-98, #f8f9fa);
        border-color: var(--clr-0-l-85, #dee2e6);
      }
      
      .user-info {
        flex: 1;
        min-width: 0;
        
        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--clr-6, #0066cc);
          color: var(--clr-1, white);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9em;
          flex-shrink: 0;
        }
        
        .user-details {
          flex: 1;
          min-width: 0;
          
          .user-name {
            font-weight: 500;
            color: var(--clr-0, #1a1a1a);
            font-size: 0.95em;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .user-email {
            font-size: 0.85em;
            color: var(--clr-0-l-30, #6c757d);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
      
      .btn-remove {
        background: none;
        border: none;
        color: #dc3545;
        font-size: 1.5em;
        cursor: pointer;
        padding: 0.25em 0.5em;
        line-height: 1;
        border-radius: 4px;
        transition: all 0.2s;
        flex-shrink: 0;
        
        &:hover {
          background: #fee;
          color: #c82333;
        }
      }
    }
  }
  
  .user-picker-modal {
    height: 100%;
    width: 100%;
    background: var(--clr-1, #ffffff);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    
    .modal-header {
      padding: 1.5em 0;
      border-bottom: 1px solid var(--clr-0-l-90, #e0e0e0);
      background: var(--clr-1, #ffffff);
      flex-shrink: 0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
      
      h3 {
        margin: 0;
        font-size: 1.5em;
        font-weight: 600;
        color: var(--clr-4, #1a1a1a);
        letter-spacing: -0.01em;
      }
      
      .btn-icon {
        background: var(--clr-0-l-95, #f5f5f5);
        border: 1px solid var(--clr-0-l-90, #e0e0e0);
        font-size: 1.5em;
        cursor: pointer;
        color: var(--clr-0-l-40, #666);
        padding: 0.4em 0.6em;
        line-height: 1;
        border-radius: 6px;
        transition: all 0.2s;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        &:hover {
          background-color: var(--clr-0-l-90, #e8e8e8);
          color: var(--clr-0, #333);
          border-color: var(--clr-0-l-80, #d0d0d0);
        }
      }
    }
    
    .modal-content {
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      padding: 0;
      min-height: 0; // Important for flex children with overflow
      
      .search-container {
        padding: 1.5em 2.5em 1em 2.5em;
        background: var(--clr-1, #ffffff);
        border-bottom: 1px solid var(--clr-0-l-90, #e0e0e0);
        flex-shrink: 0;
        
        .search-input {
          width: 100%;
          max-width: 500px;
        }
      }
      
      .loading-state,
      .empty-state {
        flex: 1;
        padding: 4em;
        color: #999;
        text-align: center;
        font-size: 1.1em;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .users-table-container {
        flex: 1;
        overflow: auto;
        padding: 1.5em 0 2.5em 0;
        background: var(--clr-1, #ffffff);
        min-height: 0; // Important for flex children with overflow
      }
    }
    
    .users-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      background: var(--clr-1, white);
      font-size: 14px;
      border: 1px solid var(--clr-0-l-90, #e0e0e0);
      border-radius: 8px;
      overflow: hidden;
      
      thead {
        background: var(--clr-0-l-98, #f8f9fa);
        position: sticky;
        top: -1.5em; // Account for container padding
        z-index: 10;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        
        th {
          padding: 1.25em 1.5em;
          text-align: right;
          font-weight: 600;
          font-size: 0.85em;
          color: var(--clr-0-l-30, #495057);
          border-bottom: 2px solid var(--clr-0-l-85, #dee2e6);
          white-space: nowrap;
          background: var(--clr-0-l-98, #f8f9fa);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.8em;
          
          &:first-child {
            border-top-right-radius: 8px;
          }
          
          &:last-child {
            border-top-left-radius: 8px;
          }
          
          &.col-checkbox {
            width: 70px;
            text-align: center;
            padding: 1.25em 1em;
          }
          
          &.col-name {
            width: 25%;
            min-width: 180px;
          }
          
          &.col-email {
            width: 30%;
            min-width: 220px;
          }
          
          &.col-mobile {
            width: 20%;
            min-width: 140px;
          }
          
          &.col-roles {
            width: 25%;
            min-width: 180px;
          }
        }
      }
      
      tbody {
        .user-row {
          cursor: pointer;
          transition: all 0.15s ease;
          border-bottom: 1px solid var(--clr-0-l-95, #f0f0f0);
          background: var(--clr-1, white);
          
          &:hover {
            background-color: var(--clr-0-l-98, #f8f9fa);
          }
          
          &.selected {
            background-color: var(--clr-6-l-95, #e7f3ff);
            border-left: 3px solid var(--clr-6, #0066cc);
            
            &:hover {
              background-color: var(--clr-6-l-90, #d6ebff);
            }
          }
          
          &:last-child {
            td {
              border-bottom: none;
            }
          }
          
          td {
            padding: 1.25em 1.5em;
            vertical-align: middle;
            border-bottom: 1px solid var(--clr-0-l-95, #f0f0f0);
            
            &.col-checkbox {
              text-align: center;
              width: 70px;
              padding: 1.25em 1em;
            }
            
            .user-name {
              font-weight: 500;
              color: var(--clr-0, #212529);
              font-size: 0.95em;
              line-height: 1.4;
            }
            
            .user-email {
              color: var(--clr-0-l-30, #6c757d);
              font-size: 0.9em;
              word-break: break-word;
              line-height: 1.4;
            }
            
            .user-mobile {
              color: var(--clr-0-l-30, #6c757d);
              font-size: 0.9em;
              font-family: 'Courier New', monospace;
              line-height: 1.4;
            }
            
            .user-roles {
              display: flex;
              flex-wrap: wrap;
              gap: 0.5em;
              
              .role-badge {
                display: inline-block;
                padding: 0.4em 0.9em;
                background-color: var(--clr-6-l-90, #e3f2fd);
                color: var(--clr-6, #1565c0);
                border-radius: 12px;
                font-size: 0.75em;
                font-weight: 600;
                border: none;
                white-space: nowrap;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
              }
              
              .no-roles {
                color: var(--clr-0-l-50, #adb5bd);
                font-style: italic;
                font-size: 0.85em;
              }
            }
          }
        }
      }
    }
    
    .checkbox-input {
      width: 20px;
      height: 20px;
      cursor: pointer;
      accent-color: var(--clr-6, #0066cc);
      margin: 0;
    }
    
    .modal-footer {
      padding: 0.5em 2.5em 0.5em 2.5em;
      background: transparent;
      flex-shrink: 0;
      margin-top: auto; // Ensure footer stays at bottom
      z-index: 10; // Ensure footer is above content
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 1em;
      
      .btn {
        min-width: 120px;
        padding: 0.75em 1.5em;
        font-weight: 500;
        border-radius: 6px;
        transition: all 0.2s;
        
        &.primary {
          background: var(--clr-6, #0066cc);
          color: var(--clr-1, white);
          border: none;
          
          &:hover {
            background: var(--clr-6-d-10, #0052a3);
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          }
        }
        
        &:not(.primary) {
          background: var(--clr-0-l-98, #f8f9fa);
          color: var(--clr-0-l-30, #495057);
          border: 1px solid var(--clr-0-l-85, #dee2e6);
          
          &:hover {
            background: var(--clr-0-l-95, #e9ecef);
            border-color: var(--clr-0-l-80, #ced4da);
          }
        }
      }
    }
  }
}
</style>
